import { Track } from "@/src/lib/tracking";
/**
 * Update Favorite Handler - 更新收藏状态处理器
 *
 * 负责处理 updateFavoriteStatus API 的完整逻辑
 */

import type { FavoritesCenterState } from "../types";
import { OperationDebouncer } from "../managers/OperationDebouncer";
import { OptimisticUpdateManager } from "../managers/OptimisticUpdateManager";
import { UserStateManager } from "../managers/UserStateManager";
import { updateVersionedData } from "../managers/VersionedDataManager";

export class UpdateFavoriteHandler {
  constructor(
    private userStateManager: UserStateManager,
    private optimisticUpdateManager: OptimisticUpdateManager,
    private operationDebouncer: OperationDebouncer,
    private state: FavoritesCenterState,
    private getCurrentUserId: () => string | null,
    private updateUserFoldersAssetCount: (
      folderId: string,
      action: "ADD" | "REMOVE",
    ) => void,
  ) {}

  /**
   * Execute update favorite status
   */
  execute(
    contentId: string,
    folderId: string,
    action: "ADD" | "REMOVE",
    assetType: "TOKEN" | "STOCK" = "TOKEN",
  ): void {
    try {
      // Ensure user is logged in
      if (!this.getCurrentUserId()) {
        Track.log(
          "warn",
          "UpdateFavoriteHandler",
          "User not logged in, cannot update favorite status",
        );
        return;
      }

      // 1. Immediately update local state (optimistic update)
      const currentState = this.state.favoriteStates.data[contentId] || [];
      let newState: string[];
      if (action === "ADD") {
        newState = currentState.includes(folderId)
          ? currentState
          : [...currentState, folderId];
      } else {
        newState = currentState.filter((id) => id !== folderId);
      }
      // 2. Use versioned data update mechanism
      const newData = { ...this.state.favoriteStates.data };
      newData[contentId] = newState;

      const updated = updateVersionedData(this.state.favoriteStates, newData);
      if (updated) {
        // 3. Update source data
        const currentUserId = this.getCurrentUserId();
        if (!currentUserId) {
          return;
        }

        const userData =
          this.userStateManager.getOrCreateUserData(currentUserId);
        userData.favoriteStates.set(contentId, newState);
        userData.lastAccess = Date.now();

        // 3.1 Optimistic update allFavoriteData (summary and cache)
        this.optimisticUpdateManager.updateAllFavoriteData([
          { key: contentId, value: newState },
        ]);

        // 4. Optimistic update asset_count in userFolders
        this.updateUserFoldersAssetCount(folderId, action);

        // 5. Add detailed debug logs

        // 6. Record operation history and async call API (with smart debounce)
        this.operationDebouncer.recordOperationHistory(
          contentId,
          folderId,
          action,
        );
        this.operationDebouncer.smartDebouncedApiCall(
          contentId,
          folderId,
          action,
          assetType,
        );

        Track.log(
          "info",
          "UpdateFavoriteHandler",
          `Updated favorite status: ${contentId} ${action} ${folderId}`,
        );
      }
    } catch (error) {
      Track.error(error, {
        tag: "UpdateFavoriteHandler",
        msg: "Failed to update favorite status:",
      });
      throw error;
    }
  }
}
